import { RJSFSchema, WidgetProps } from '@rjsf/utils';
import { Card, Typography, Radio, RadioGroup, FormControlLabel, Box, Skeleton, Grid, CardContent } from '@mui/material';

import { LLMModelSpecification, ModelType } from 'api/model';
import { FormBlockContext } from 'components/form/FormBlock';
import useModelSpecifications from 'api/model-specifications/useModelSpecifications';
import useOllamaSyncedModels from 'api/prompts-models/useOllamaSyncedModels';
import ModelSpecificationCard from './ModelSpecificationCard';

type LoadingModelsPlaceholderProps = {
  cards?: number;
  height?: number;
  title?: string;
};

function LoadingModelsPlaceholder({
  cards = 6,
  height = 200,
  title = 'Loading Models...',
}: LoadingModelsPlaceholderProps) {
  return (
    <Box className="p-6">
      <Typography variant="h6" gutterBottom color="text.secondary">
        {title}
      </Typography>
      <Grid container spacing={2}>
        {Array.from({ length: cards }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card variant="outlined" sx={{ height }}>
              <CardContent>
                <Skeleton variant="text" width="80%" height={32} />
                <Skeleton variant="text" width="60%" height={20} className="mt-1" />
                <Box className="flex gap-2 mt-2">
                  <Skeleton variant="rectangular" width={60} height={24} className="rounded" />
                  <Skeleton variant="rectangular" width={80} height={24} className="rounded" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function ModelCardSelector({
  value,
  onChange,
  formContext,
}: WidgetProps<string, RJSFSchema, FormBlockContext>) {
  const provider = (formContext?.extra?.provider ?? 'openai') as ModelType;
  const currentBaseUrl = provider === ModelType.OLLAMA ? (formContext?.extra?.base_url ?? '') : '';
  const { modelSpecifications } = useModelSpecifications(provider);
  const { serverModelNames } = useOllamaSyncedModels(currentBaseUrl);

  if (!modelSpecifications) {
    return <LoadingModelsPlaceholder cards={6} height={212} title="Loading Models..." />;
  }

  const serverNameSet = provider === ModelType.OLLAMA ? new Set(serverModelNames) : null;

  const grouped = modelSpecifications.reduce<Record<string, LLMModelSpecification[]>>((acc, model) => {
    const group = model.group ?? 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(model);
    return acc;
  }, {});

  if (Object.keys(grouped).length === 0) {
    return (
      <Box>
        <Typography color="text.disabled">No models available for the selected provider.</Typography>
        {provider === ModelType.OLLAMA && (
          <Typography color="text.disabled" variant="body2" className="mt-1">
            You can sync models available on your Ollama server by entering the server URL above and clicking the sync
            button.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box className="p-0">
      <RadioGroup value={value as string} onChange={(e) => onChange(e.target.value)}>
        {Object.entries(grouped).map(([groupName, models]) => (
          <Box key={groupName} className="mt-2">
            <Typography variant="h6" color="text.secondary" className="mb-2">
              {groupName}
            </Typography>

            <Grid container spacing={2}>
              {models.map((model: LLMModelSpecification) => {
                const isSelected = value === model.name;
                const isOnServer = serverNameSet?.has(model.name) ?? false;

                return (
                  <Grid item xs={12} sm={6} md={4} key={model.name}>
                    <FormControlLabel
                      value={model.name}
                      control={<Radio className="hidden" />}
                      label={<ModelSpecificationCard model={model} selected={isSelected} isOnServer={isOnServer} />}
                      className="m-0 w-full"
                      sx={{ '& .MuiFormControlLabel-label': { width: '100%', minWidth: 0 } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </RadioGroup>
    </Box>
  );
}
